import React, { useState, useEffect, useCallback } from 'react';

import { BACKEND_URL as GREENWICH_BACKEND_URL } from '../utils/network.js';
import { localizeMessage } from '../utils/toast.js';
const API_BASE = process.env.REACT_APP_API_URL || `${GREENWICH_BACKEND_URL}/api`;
const MAX_QUESTIONS = 20;
const QUESTION_MAX_LEN = 150;
const DEFAULT_QUESTIONS = [
  { text: 'Full name and phone number', required: true },
  { text: 'Can you confirm you are available at the program location and dates?', required: true },
  { text: 'Why do you want to join this program?', required: true },
  { text: 'Any additional information (optional)', required: false },
];

const STATUS_CONFIG = {
  pending:  { label: 'Pending',  color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: '⏳' },
  accepted: { label: 'Accepted', color: '#16a34a', bg: '#dcfce7', border: '#86efac', icon: '✅' },
  rejected: { label: 'Rejected', color: '#dc2626', bg: '#fee2e2', border: '#fecaca', icon: '❌' },
  attended: { label: 'Attended', color: '#2563eb', bg: '#dbeafe', border: '#93c5fd', icon: '🎓' },
};

function formatDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return d; }
}

function StarPicker({ value, onChange }) {
  return (
    <div className="app-star-picker">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          type="button"
          key={n}
          className={`app-star${n <= value ? ' app-star-filled' : ''}`}
          onClick={() => onChange(n)}
          aria-label={`${n} star`}
        >
          ★
        </button>
      ))}
    </div>
  );
}

function RespondBox({ application, onDone }) {
  const [mode, setMode] = useState(null); // 'accepted' | 'rejected' | null
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (status) => {
    setSubmitting(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_BASE}/applications/${application.id}/respond`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, message: message.trim() }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        onDone(data.application || { ...application, status, organizerMessage: message.trim() });
      }
    } catch {}
    setSubmitting(false);
    setMode(null);
    setMessage('');
  };

  if (mode) {
    return (
      <div className="app-respond-box">
        <textarea
          className="app-textarea"
          rows={2}
          placeholder="Optional message to the applicant…"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        <div className="app-respond-actions">
          <button className="app-btn-ghost" onClick={() => { setMode(null); setMessage(''); }} disabled={submitting}>Cancel</button>
          <button
            className={mode === 'accepted' ? 'app-btn-accept' : 'app-btn-reject'}
            onClick={() => submit(mode)}
            disabled={submitting}
          >
            {submitting ? '…' : mode === 'accepted' ? 'Confirm Accept' : 'Confirm Reject'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-card-actions">
      <button className="app-btn-accept" onClick={() => setMode('accepted')}>Accept</button>
      <button className="app-btn-reject" onClick={() => setMode('rejected')}>Reject</button>
    </div>
  );
}

function AttendedButton({ application, onDone }) {
  const [loading, setLoading] = useState(false);
  const markAttended = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_BASE}/applications/${application.id}/attended`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        onDone(data.application || { ...application, status: 'attended' });
      }
    } catch {}
    setLoading(false);
  };
  return (
    <div className="app-card-actions">
      <button className="app-btn-attended" onClick={markAttended} disabled={loading}>
        {loading ? '…' : 'Mark as attended'}
      </button>
    </div>
  );
}

function CertificateForm({ application, onIssued }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setError('');
    if (!file) { setError('A PDF certificate file is required.'); return; }
    if (!rating) { setError('Please select a star rating.'); return; }
    setSubmitting(true);
    const token = localStorage.getItem('token');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', title || '');
      fd.append('rating', String(rating));
      fd.append('comment', comment || '');
      const res = await fetch(`${API_BASE}/applications/${application.id}/certificate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        onIssued();
      } else {
        setError(data.error || 'Failed to issue certificate.');
      }
    } catch {
      setError('Network error while issuing certificate.');
    }
    setSubmitting(false);
  };

  return (
    <div className="app-cert-form">
      <h4 className="app-cert-heading">🏅 Issue certificate & review</h4>
      <div className="app-field">
        <label>Certificate PDF <span className="app-req">*</span></label>
        <input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </div>
      <div className="app-field">
        <label>Certificate title (optional)</label>
        <input className="app-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Certificate of Participation" />
      </div>
      <div className="app-field">
        <label>Rating <span className="app-req">*</span></label>
        <StarPicker value={rating} onChange={setRating} />
      </div>
      <div className="app-field">
        <label>Comment (optional)</label>
        <textarea className="app-textarea" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Leave a comment about the applicant…" />
      </div>
      {error && <div className="app-error">{localizeMessage(error)}</div>}
      <div className="app-cert-form-actions">
        <button className="app-btn-primary" onClick={submit} disabled={submitting}>
          {submitting ? 'Issuing…' : 'Issue certificate'}
        </button>
      </div>
    </div>
  );
}

function ApplicationCard({ application, onUpdate }) {
  const a = application;
  const cfg = STATUS_CONFIG[a.status] || STATUS_CONFIG.pending;
  const name = [a.applicant?.name, a.applicant?.surname].filter(Boolean).join(' ') || a.applicant?.username || 'Applicant';

  return (
    <div className="app-card">
      <div className="app-card-top">
        <div className="app-applicant">
          {a.applicant?.profileImage ? (
            <img src={a.applicant.profileImage} alt="" className="app-avatar" onError={(e) => { e.target.style.display = 'none'; }} />
          ) : (
            <div className="app-avatar-fallback">{name.charAt(0).toUpperCase()}</div>
          )}
          <div>
            <div className="app-applicant-name">{name}</div>
            {a.createdAt && <div className="app-applicant-date">Applied {formatDate(a.createdAt)}</div>}
          </div>
        </div>
        <span className="app-status-pill" style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.border }}>
          {cfg.icon} {cfg.label}
        </span>
      </div>

      {a.answers?.length > 0 && (
        <div className="app-answers">
          {a.answers.map((qa, i) => (
            <div className="app-answer-row" key={i}>
              <div className="app-answer-q">{qa.question}</div>
              <div className="app-answer-a">{qa.answer || <em>No answer</em>}</div>
            </div>
          ))}
        </div>
      )}

      {a.fileUrl && (
        <a className="app-file-link" href={a.fileUrl} target="_blank" rel="noreferrer">
          📎 {a.fileName || 'Attached file'}
        </a>
      )}

      {a.organizerMessage && (
        <div className="app-org-message">Your message: {a.organizerMessage}</div>
      )}

      {a.status === 'pending' && (
        <RespondBox application={a} onDone={(updated) => onUpdate({ ...a, ...updated })} />
      )}

      {a.status === 'accepted' && (
        <AttendedButton application={a} onDone={(updated) => onUpdate({ ...a, ...updated })} />
      )}

      {a.status === 'attended' && !a.certificateIssued && (
        <CertificateForm application={a} onIssued={() => onUpdate({ ...a, certificateIssued: true })} />
      )}

      {a.status === 'attended' && a.certificateIssued && (
        <div className="app-cert-badge">✅ Certificate issued</div>
      )}
    </div>
  );
}

function QuestionBuilder({ questions, setQuestions }) {
  const addQuestion = () => {
    setQuestions((p) => (p.length >= MAX_QUESTIONS ? p : [...p, { text: '', required: true }]));
  };
  const updateQuestion = (idx, patch) => {
    setQuestions((p) => p.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  };
  const removeQuestion = (idx) => {
    setQuestions((p) => p.filter((_, i) => i !== idx));
  };

  return (
    <div className="app-qbuilder">
      {questions.map((q, idx) => (
        <div className="app-qrow" key={idx}>
          <div className="app-qinput-wrap">
            <input
              className="app-input app-qinput"
              placeholder={`Question ${idx + 1}`}
              value={q.text}
              maxLength={QUESTION_MAX_LEN}
              onChange={(e) => updateQuestion(idx, { text: e.target.value.slice(0, QUESTION_MAX_LEN) })}
            />
            <span className="app-qcharcount">{q.text.length}/{QUESTION_MAX_LEN}</span>
          </div>
          <label className="app-qrequired">
            <input type="checkbox" checked={q.required} onChange={(e) => updateQuestion(idx, { required: e.target.checked })} />
            Required
          </label>
          <button type="button" className="app-qremove" onClick={() => removeQuestion(idx)} aria-label="Remove question">✕</button>
        </div>
      ))}
      {questions.length < MAX_QUESTIONS && (
        <button type="button" className="app-qadd" onClick={addQuestion}>+ Add question</button>
      )}
      <p className="app-qnote">Applicants will always be able to attach a file (up to 10MB) when applying.</p>
    </div>
  );
}

function ApplicationFormEditor({ programId }) {
  const [enabled, setEnabled] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const token = localStorage.getItem('token');
      try {
        const res = await fetch(`${API_BASE}/programs/${programId}/application-form`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json().catch(() => ({}));
          if (!cancelled) {
            const form = data.applicationForm || { enabled: false, questions: [] };
            setEnabled(!!form.enabled);
            setQuestions(form.questions?.length ? form.questions.map(q => ({ text: q.text || '', required: q.required !== false })) : []);
          }
        }
      } catch {}
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [programId]);

  const toggle = (checked) => {
    setEnabled(checked);
    if (checked && questions.length === 0) setQuestions(DEFAULT_QUESTIONS.map((q) => ({ ...q })));
  };

  const save = async () => {
    setSaving(true);
    setSaved(false);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_BASE}/programs/${programId}/application-form`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled,
          questions: enabled ? questions.filter((q) => q.text.trim()) : [],
        }),
      });
      if (res.ok) setSaved(true);
    } catch {}
    setSaving(false);
  };

  if (loading) return <div className="app-form-editor-loading">Loading application form…</div>;

  return (
    <div className="app-form-editor">
      <label className="app-form-editor-toggle">
        <input type="checkbox" checked={enabled} onChange={(e) => toggle(e.target.checked)} />
        Let users apply directly on the platform
      </label>
      {enabled && <QuestionBuilder questions={questions} setQuestions={setQuestions} />}
      <div className="app-form-editor-actions">
        <button className="app-btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save application form'}
        </button>
        {saved && <span className="app-saved-note">Saved ✓</span>}
      </div>
    </div>
  );
}

function ApplicantsPanel({ programId, programTitle, onClose }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list' | 'form'

  const loadApplications = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_BASE}/programs/${programId}/applications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        setApplications(Array.isArray(data.applications) ? data.applications : []);
      }
    } catch {}
    setLoading(false);
  }, [programId]);

  useEffect(() => { loadApplications(); }, [loadApplications]);

  const updateApplication = (id, patch) => {
    setApplications((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  };

  return (
    <div className="app-overlay" onClick={onClose}>
      <div className="app-panel" onClick={(e) => e.stopPropagation()}>
        <div className="app-panel-header">
          <div>
            <h3 className="app-panel-title">🧾 Applicants</h3>
            {programTitle && <p className="app-panel-sub">{programTitle}</p>}
          </div>
          <button className="app-close-btn" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="app-panel-tabs">
          <button className={`app-tab${view === 'list' ? ' app-tab-active' : ''}`} onClick={() => setView('list')}>Applicants</button>
          <button className={`app-tab${view === 'form' ? ' app-tab-active' : ''}`} onClick={() => setView('form')}>Edit application form</button>
        </div>

        <div className="app-panel-body">
          {view === 'form' ? (
            <ApplicationFormEditor programId={programId} />
          ) : loading ? (
            <div className="app-loading"><div className="app-spinner" /><p>Loading applicants…</p></div>
          ) : applications.length === 0 ? (
            <div className="app-empty">
              <div className="app-empty-icon">📭</div>
              <h4>No applicants yet</h4>
              <p>Once someone applies on the platform, they'll show up here.</p>
            </div>
          ) : (
            <div className="app-list">
              {applications.map((a) => (
                <ApplicationCard key={a.id} application={a} onUpdate={(patch) => updateApplication(a.id, patch)} />
              ))}
            </div>
          )}
        </div>

        <style>{`
          :root{--green:#32614d;--green-dark:#22432f;--green-light:#e8f2ed;--amber:#faa002;}
          .app-overlay{position:fixed;inset:0;background:rgba(20,35,25,0.45);display:flex;align-items:center;justify-content:center;z-index:10000;padding:20px;font-family:'Plus Jakarta Sans',sans-serif;}
          .app-panel{background:#f2f6f3;border-radius:16px;width:100%;max-width:720px;max-height:88vh;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,0.25);}
          .app-panel-header{display:flex;align-items:center;justify-content:space-between;padding:18px 20px;background:white;border-bottom:1px solid #dceae2;}
          .app-panel-title{font-size:17px;font-weight:800;color:var(--green-dark);}
          .app-panel-sub{font-size:12px;color:#9abda8;margin-top:2px;}
          .app-close-btn{width:32px;height:32px;border-radius:50%;border:1.5px solid #d4e8de;background:white;color:var(--green);cursor:pointer;font-size:13px;}
          .app-close-btn:hover{background:var(--green-light);}
          .app-panel-tabs{display:flex;gap:6px;padding:12px 20px 0;background:white;border-bottom:1px solid #dceae2;}
          .app-tab{padding:9px 16px;border:none;background:transparent;color:#6b8f78;font-size:13px;font-weight:700;cursor:pointer;border-bottom:2px solid transparent;font-family:inherit;}
          .app-tab-active{color:var(--green-dark);border-bottom-color:var(--green);}
          .app-panel-body{flex:1;overflow-y:auto;padding:18px 20px;}
          .app-loading{display:flex;flex-direction:column;align-items:center;gap:10px;padding:50px 20px;}
          .app-spinner{width:32px;height:32px;border:3px solid #e2e8f0;border-top-color:var(--green);border-radius:50%;animation:app-spin 0.8s linear infinite;}
          @keyframes app-spin{to{transform:rotate(360deg)}}
          .app-empty{text-align:center;padding:50px 20px;}
          .app-empty-icon{font-size:40px;margin-bottom:12px;}
          .app-empty h4{font-size:15px;font-weight:800;color:var(--green-dark);margin-bottom:6px;}
          .app-empty p{color:#9abda8;font-size:13px;}
          .app-list{display:flex;flex-direction:column;gap:12px;}
          .app-card{background:white;border-radius:12px;box-shadow:0 2px 10px rgba(50,97,77,0.08);padding:14px 16px;}
          .app-card-top{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:10px;}
          .app-applicant{display:flex;align-items:center;gap:10px;}
          .app-avatar{width:36px;height:36px;border-radius:50%;object-fit:cover;}
          .app-avatar-fallback{width:36px;height:36px;border-radius:50%;background:var(--green-light);color:var(--green-dark);display:flex;align-items:center;justify-content:center;font-weight:800;font-size:14px;}
          .app-applicant-name{font-size:13.5px;font-weight:700;color:var(--green-dark);}
          .app-applicant-date{font-size:11px;color:#9abda8;}
          .app-status-pill{font-size:11.5px;font-weight:700;padding:5px 10px;border-radius:20px;border:1px solid;white-space:nowrap;}
          .app-answers{background:#f4f9f6;border-radius:8px;padding:10px 12px;margin-bottom:10px;display:flex;flex-direction:column;gap:8px;}
          .app-answer-row{font-size:12.5px;}
          .app-answer-q{font-weight:700;color:#6b8f78;margin-bottom:2px;}
          .app-answer-a{color:#2d4a38;white-space:pre-wrap;}
          .app-file-link{display:inline-flex;align-items:center;gap:5px;font-size:12px;color:var(--green);font-weight:600;text-decoration:none;margin-bottom:10px;}
          .app-file-link:hover{text-decoration:underline;}
          .app-org-message{font-size:12px;color:#6b8f78;background:#f4f9f6;border-radius:8px;padding:8px 10px;margin-bottom:10px;}
          .app-card-actions{display:flex;gap:8px;}
          .app-btn-accept{padding:8px 16px;border-radius:8px;border:none;background:#16a34a;color:white;font-size:12.5px;font-weight:700;cursor:pointer;font-family:inherit;}
          .app-btn-accept:hover{background:#15803d;}
          .app-btn-reject{padding:8px 16px;border-radius:8px;border:1.5px solid #fecaca;background:#fef2f2;color:#dc2626;font-size:12.5px;font-weight:700;cursor:pointer;font-family:inherit;}
          .app-btn-reject:hover{background:#fee2e2;}
          .app-btn-attended{padding:8px 16px;border-radius:8px;border:none;background:#2563eb;color:white;font-size:12.5px;font-weight:700;cursor:pointer;font-family:inherit;}
          .app-btn-attended:hover{background:#1d4ed8;}
          .app-btn-ghost{padding:8px 16px;border-radius:8px;border:1.5px solid #d4e8de;background:transparent;color:#6b8f78;font-size:12.5px;font-weight:700;cursor:pointer;font-family:inherit;}
          .app-btn-primary{padding:9px 18px;border-radius:8px;border:none;background:var(--green);color:white;font-size:13px;font-weight:700;cursor:pointer;font-family:inherit;}
          .app-btn-primary:hover:not(:disabled){background:var(--green-dark);}
          .app-btn-primary:disabled{opacity:0.6;cursor:not-allowed;}
          .app-respond-box{display:flex;flex-direction:column;gap:8px;}
          .app-respond-actions{display:flex;gap:8px;justify-content:flex-end;}
          .app-textarea{width:100%;padding:8px 10px;border:1.5px solid #d4e8de;border-radius:7px;font-size:12.5px;font-family:inherit;outline:none;color:#2d4a38;background:white;resize:vertical;}
          .app-textarea:focus{border-color:var(--green);}
          .app-cert-badge{display:inline-block;padding:7px 12px;background:#dcfce7;color:#16a34a;border-radius:8px;font-size:12px;font-weight:700;}
          .app-cert-form{background:#f4f9f6;border-radius:10px;padding:14px;margin-top:4px;}
          .app-cert-heading{font-size:13px;font-weight:800;color:var(--green-dark);margin-bottom:12px;}
          .app-field{display:flex;flex-direction:column;gap:5px;margin-bottom:12px;}
          .app-field label{font-size:11px;font-weight:700;color:#9abda8;text-transform:uppercase;letter-spacing:0.04em;}
          .app-req{color:#dc2626;}
          .app-input{padding:8px 10px;border:1.5px solid #d4e8de;border-radius:7px;font-size:13px;font-family:inherit;outline:none;color:#2d4a38;background:white;}
          .app-input:focus{border-color:var(--green);}
          .app-star-picker{display:flex;gap:4px;}
          .app-star{font-size:22px;background:none;border:none;cursor:pointer;color:#d4e8de;line-height:1;padding:0;}
          .app-star-filled{color:var(--amber);}
          .app-error{padding:8px 10px;background:#fef2f2;border:1px solid #fecaca;border-radius:7px;color:#dc2626;font-size:12px;margin-bottom:10px;}
          .app-cert-form-actions{display:flex;justify-content:flex-end;}
          .app-form-editor-loading{padding:30px;text-align:center;color:#9abda8;font-size:13px;}
          .app-form-editor{display:flex;flex-direction:column;gap:14px;}
          .app-form-editor-toggle{display:flex;align-items:center;gap:10px;font-size:13.5px;font-weight:700;color:#2d4a38;cursor:pointer;}
          .app-qbuilder{display:flex;flex-direction:column;gap:10px;background:white;border:1.5px solid #dceae2;border-radius:10px;padding:14px;}
          .app-qrow{display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
          .app-qinput-wrap{flex:1;min-width:160px;display:flex;flex-direction:column;gap:2px;}
          .app-qinput{flex:1;min-width:160px;}
          .app-qcharcount{font-size:10.5px;color:#9abda8;align-self:flex-end;}
          .app-qrequired{display:flex;align-items:center;gap:5px;font-size:12px;font-weight:600;color:#6b8f78;white-space:nowrap;}
          .app-qremove{width:30px;height:30px;flex-shrink:0;border-radius:8px;border:1.5px solid #fecaca;background:#fef2f2;color:#dc2626;font-weight:700;cursor:pointer;}
          .app-qremove:hover{background:#fee2e2;}
          .app-qadd{align-self:flex-start;padding:8px 16px;background:transparent;border:1.5px dashed var(--green);border-radius:8px;color:var(--green);font-size:12.5px;font-weight:700;cursor:pointer;font-family:inherit;}
          .app-qadd:hover{background:var(--green-light);}
          .app-qnote{font-size:12px;color:#9abda8;font-style:italic;}
          .app-form-editor-actions{display:flex;align-items:center;gap:10px;}
          .app-saved-note{font-size:12px;color:#16a34a;font-weight:700;}
          @media(max-width:640px){
            .app-panel{max-height:94vh;}
            .app-card-top{flex-direction:column;align-items:flex-start;}
          }
        `}</style>
      </div>
    </div>
  );
}

export default ApplicantsPanel;
